/* eslint-disable @angular-eslint/prefer-standalone */
import {
  Component,
  EventEmitter,
  inject,
  Output,
  ViewChild
} from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ChatNewService,
  ChatChannel,
  ArchivedChatData,
  OpportunityChannel
} from 'sustainment-component';
import { AutoComplete, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { ViewSwitcherService } from 'src/app/services/view-switcher.service';

interface ChatAutocompleteItem {
  label: string;
  sublabel?: string;
  projectId?: string;
  value: ChatChannel | ArchivedChatData;
}

interface ChatAutocompleteOption {
  label: string;
  value: string;
  items: ChatAutocompleteItem[];
}

@Component({
  selector: 'app-chat-search',
  templateUrl: './chat-search.component.html',
  styleUrls: ['./chat-search.component.scss'],
  standalone: false
})
export class ChatSearchComponent {
  private _viewSwitcherService = inject(ViewSwitcherService);

  @Output() public selectChat = new EventEmitter<{
    channel: ChatChannel | ArchivedChatData;
    projectId?: string;
  }>();
  public autocompleteValues$: Observable<ChatAutocompleteOption[]>;
  public selectedValue: ChatAutocompleteItem | null;
  @ViewChild('selectAutocomplete') public selectAutocomplete: AutoComplete;

  public currentAppView = this._viewSwitcherService.currentAppView;

  public constructor(private chatService: ChatNewService) {}

  public searchConversations(filter: string): void {
    const lowercasedFilter = filter.toLowerCase();
    this.autocompleteValues$ = this.chatService.channels$.pipe(
      map((channels) => {
        const companyChannelsAlreadyLoaded =
          channels.companies.map<ChatAutocompleteItem>((chn) => ({
            label: chn.name,
            value: chn
          }));

        const loadedChannelAliases = new Set<string>(
          companyChannelsAlreadyLoaded.map((chn) =>
            typeof chn.value !== 'string' ? chn.value.alias : ''
          )
        );

        const archivedChats = channels.archivedChats
          ?.filter((chat) => !loadedChannelAliases.has(chat.alias))
          ?.map<ChatAutocompleteItem>((chat) => ({
            label: chat.name,
            value: chat
          }));

        const companies = archivedChats
          ? [...companyChannelsAlreadyLoaded, ...archivedChats].filter((chat) =>
              chat.label.toLowerCase().includes(lowercasedFilter)
            )
          : [];

        const filterProjects = (
          projects:
            | typeof channels.sourcingProjects
            | typeof channels.procurementProjects
        ): {
          label: string;
          sublabel?: string;
          value: ChatChannel | ArchivedChatData;
          projectId?: string;
        }[] => {
          return projects.reduce((accumulator, project) => {
            const filteredChannels = project.name
              .toLowerCase()
              .includes(lowercasedFilter)
              ? project.channels
              : project.channels.filter((x) =>
                  x.name.toLowerCase().includes(lowercasedFilter)
                );

            return accumulator.concat(
              filteredChannels.map((ch) => ({
                label: project.name,
                sublabel: ch.name,
                value: ch,
                projectId: project.projectId
              }))
            );
          }, [] as ChatAutocompleteItem[]);
        };

        const sourcingProjects = filterProjects(channels.sourcingProjects);
        const procurementProjects = filterProjects(
          channels.procurementProjects
        );

        const filterOpportunities = (
          opportunities:
            | typeof channels.sourcingOpportunities
            | typeof channels.procurementOrders
        ): {
          label: string;
          sublabel: string;
          value: OpportunityChannel;
          projectId: string;
        }[] => {
          return opportunities
            .filter(
              (x) =>
                x.name.toLowerCase().includes(lowercasedFilter) ||
                x.vendorName.toLowerCase().includes(lowercasedFilter)
            )
            .map((c) => ({
              label: c.name,
              sublabel: c.vendorName,
              value: c,
              projectId: c.opportunityId
            }));
        };

        const sourcingOpportunities = filterOpportunities(
          channels.sourcingOpportunities
        );
        const procurementOrders = filterOpportunities(
          channels.procurementOrders
        );

        const result: ChatAutocompleteOption[] = [];

        if (sourcingProjects.length && this.currentAppView() === 'buyer') {
          result.push({
            label: 'Sourcing Chats',
            value: 'projects',
            items: sourcingProjects
          });
        }

        if (procurementProjects.length && this.currentAppView() === 'buyer') {
          result.push({
            label: 'Procurement Chats',
            value: 'projects',
            items: procurementProjects
          });
        }

        if (
          sourcingOpportunities.length &&
          this.currentAppView() === 'supplier'
        ) {
          result.push({
            label: 'Opportunity Chats',
            value: 'opportunities',
            items: sourcingOpportunities
          });
        }

        if (procurementOrders.length && this.currentAppView() === 'supplier') {
          result.push({
            label: 'Orders Chats',
            value: 'opportunities',
            items: procurementOrders
          });
        }

        if (companies?.length) {
          result.push({
            label: 'Company Chats',
            value: 'companies',
            items: companies
          });
        }
        return result;
      })
    );
  }

  public onSelectChannel(e: AutoCompleteSelectEvent): void {
    const item = e.value as ChatAutocompleteItem;
    this.selectChat.emit({ channel: item.value, projectId: item.projectId });
    this.selectedValue = null!;
    this.selectAutocomplete.clear();
  }

  public removeFocusAutocomplete(): void {
    setTimeout(() => {
      const autocompleteInput = document.querySelector(
        '.autocomplete input'
      ) as HTMLElement;

      if (autocompleteInput) {
        autocompleteInput.blur();
      }
    }, 0);
    document.body.focus();
  }
}
